import {
  Activity,
  Baby,
  Brain,
  Microscope,
  Salad,
  Stethoscope,
  Users,
} from './icons'

export const DOCTORS = [
  {
    slug: 'psychiatrist',
    group: 'Doctors',
    Icon: Stethoscope,
    title: 'Psychiatrist',
    tagline:
      'Diagnose, treat, and coordinate psychiatric care inside a continuous care system.',
    intro:
      'Psychiatrists at AUMHUM lead diagnostic clarity, medication management, and psychiatric oversight as part of a coordinated care team — working alongside therapists, endocrinologists, and other medical specialists.',
    role: [
      'Lead clinical diagnostic evaluation of presenting concerns',
      'Prescribe and manage psychiatric medication when clinically indicated',
      'Review and adjust treatment plans in collaboration with therapy teams',
      'Consult on complex cases across the clinical network',
    ],
    collaboration:
      'You work inside a shared clinical record with therapists, endocrinologists, and clinical coordinators. Measurement-based instruments and AI-assisted clinical summaries support your day-to-day decisions.',
    offer: [
      'Structured clinical workflow',
      'Access to integrated endocrine and medical specialists',
      'Measurement-based care tooling',
      'Continuity of care across sessions and settings',
      'Clinical supervision and peer review',
      'Flexible work across outpatient, rehabilitation, and remote care',
    ],
    cta: { label: 'Apply to join', href: null /* set below from slug */ },
  },
  {
    slug: 'pediatrician',
    group: 'Doctors',
    Icon: Baby,
    title: 'Pediatrician',
    tagline: 'Mental and developmental health across childhood and adolescence.',
    intro:
      "Pediatricians at AUMHUM support the medical side of childhood mental health — developmental, behavioural, and medical presentations — in collaboration with child psychiatrists, psychologists, and families.",
    role: [
      'Evaluate developmental, behavioural, and medical presentations',
      'Partner with child psychiatrists and psychologists on complex cases',
      'Support families in navigating coordinated care pathways',
      'Review medical contributors to mental health symptoms in children',
    ],
    collaboration:
      'You work with a multi-disciplinary team — child psychiatrists, psychologists, speech and occupational therapists — with a shared clinical view and structured care pathways.',
    offer: [
      'Multi-disciplinary paediatric care team',
      'Structured developmental and behavioural pathways',
      'Shared clinical record and care coordination',
      'Continuity across clinics, homes, and remote care',
      'Clinical supervision and peer collaboration',
      'Meaningful exposure to complex paediatric mental health',
    ],
    cta: { label: 'Apply to join', href: null /* set below from slug */ },
  },
  {
    slug: 'endocrinologist',
    group: 'Doctors',
    Icon: Microscope,
    title: 'Endocrinologist',
    tagline: 'Treat mental health at the level of biology.',
    intro:
      'Endocrinologists at AUMHUM evaluate the hormonal and metabolic drivers of mental health — thyroid, reproductive hormones, insulin and metabolic health, cortisol, and sleep endocrinology — and partner with psychiatry and therapy on integrated plans.',
    role: [
      'Assess thyroid, metabolic, reproductive, and sleep-endocrine factors',
      'Partner with psychiatry on combined pharmacological planning',
      'Order and interpret relevant workups within the care plan',
      'Review complex mind–body cases across the network',
    ],
    collaboration:
      'You work with psychiatrists, therapists, nutritionists, and physicians — contributing the biological layer of a unified treatment plan.',
    offer: [
      'Structured mind–body clinical pathway',
      'Collaboration with psychiatry and therapy teams',
      'Shared clinical record with full medical context',
      'Measurement-based tracking across biological and psychological domains',
      'Clinical supervision and peer review',
      'Opportunity to shape a new standard of integrated care',
    ],
    cta: { label: 'Apply to join', href: null /* set below from slug */ },
  },
  {
    slug: 'neurologist',
    group: 'Doctors',
    Icon: Brain,
    title: 'Neurologist',
    tagline: 'Neurological contributions to mental health and cognition.',
    intro:
      'Neurologists at AUMHUM evaluate cognitive, seizure, movement, and sleep disorders that shape mental health — and partner with psychiatry on complex cases where neurology and psychiatry intersect.',
    role: [
      'Evaluate cognitive, sleep, seizure, and movement disorders with psychiatric overlap',
      'Partner with psychiatry for complex overlap cases',
      'Contribute to multi-disciplinary case reviews',
      'Guide appropriate neurological workup within a shared plan',
    ],
    collaboration:
      'Work closely with psychiatrists, endocrinologists, and therapists — contributing neurological context to a unified clinical view.',
    offer: [
      'Multi-disciplinary clinical collaboration',
      'Structured workflows for neuro-psychiatric overlap cases',
      'Shared clinical record across specialists',
      'Measurement-based care tooling',
      'Clinical supervision and peer review',
      'Flexible work across outpatient and remote care',
    ],
    cta: { label: 'Apply to join', href: null /* set below from slug */ },
  },
  {
    slug: 'rheumatologist',
    group: 'Doctors',
    Icon: Activity,
    title: 'Rheumatologist',
    tagline: 'Autoimmune drivers of mood, fatigue, and cognition.',
    intro:
      'Rheumatologists at AUMHUM evaluate autoimmune and inflammatory conditions that can present with — or worsen — psychiatric symptoms, and collaborate on integrated mind–body treatment plans.',
    role: [
      'Assess autoimmune and inflammatory contributors to mental health',
      'Partner with endocrinology and psychiatry on combined care',
      'Review complex fatigue, pain, and cognitive presentations',
      'Contribute to multi-disciplinary case discussions',
    ],
    collaboration:
      'Work with endocrinologists, psychiatrists, and therapy teams — bringing rheumatological insight into integrated care plans.',
    offer: [
      'Multi-disciplinary clinical collaboration',
      'Structured mind–body clinical pathway',
      'Shared clinical record across specialists',
      'Measurement-based care tooling',
      'Clinical supervision and peer review',
      'Opportunity to contribute to integrated care research',
    ],
    cta: { label: 'Apply to join', href: null /* set below from slug */ },
  },
]

export const CAREGIVERS = [
  {
    slug: 'clinical-psychologist',
    group: 'Care-Givers',
    Icon: Brain,
    title: 'Clinical Psychologist',
    tagline: 'Deliver structured therapy inside a coordinated clinical system.',
    intro:
      'Clinical Psychologists at AUMHUM deliver evidence-based therapy within a care team — working alongside psychiatrists, endocrinologists, and allied professionals, with supervision and structured clinical records.',
    role: [
      'Deliver structured therapy — CBT, REBT, trauma-focused, and family work',
      'Conduct clinical assessments and diagnostic formulations',
      'Collaborate with psychiatry and medical specialists on integrated plans',
      'Participate in case reviews and clinical supervision',
    ],
    collaboration:
      'You work within a shared clinical record, with continuity of care, outcome tracking, and the full specialist network at hand when cases require it.',
    offer: [
      'Structured therapy protocols',
      'Clinical supervision and continuing development',
      'Psychiatric and medical collaboration',
      'Measurement-based outcome tracking',
      'Work across outpatient, rehabilitation, and remote settings',
      'Opportunities across regions',
    ],
    cta: { label: 'Join our network', href: null /* set below from slug */ },
  },
  {
    slug: 'counsellor',
    group: 'Care-Givers',
    Icon: Users,
    title: 'Counsellor',
    tagline: 'Support clients through evidence-informed counselling — inside a clinical system.',
    intro:
      'Counsellors at AUMHUM provide structured counselling support integrated into clinical pathways — triage, stabilisation, follow-up, and ongoing psychosocial support — with access to supervision and the wider care team.',
    role: [
      'Deliver structured counselling interventions',
      'Support stabilisation and follow-up care',
      'Triage new concerns under clinical supervision',
      'Refer into therapy and psychiatric care as clinically indicated',
    ],
    collaboration:
      "You work with therapists, psychiatrists, and clinical coordinators — fully supported by supervision and a shared view of each client's care.",
    offer: [
      'Structured counselling pathways',
      'Clinical supervision and continuing development',
      'Collaboration with therapists and psychiatrists',
      'Measurement-based progress tracking',
      'Work across outpatient and remote settings',
      'Career progression inside a clinical system',
    ],
    cta: { label: 'Join our network', href: null /* set below from slug */ },
  },
  {
    slug: 'nutritionist',
    group: 'Care-Givers',
    Icon: Salad,
    title: 'Nutritionist',
    tagline: "Nutrition's role in mental and endocrine health.",
    intro:
      'Nutritionists at AUMHUM address the dietary and metabolic factors that shape mood, anxiety, energy, and endocrine health — partnering with psychiatrists, endocrinologists, and therapists on integrated plans.',
    role: [
      'Assess nutritional and metabolic factors in mental health presentations',
      'Design nutrition plans that fit psychiatric and endocrine care',
      'Support cases involving eating patterns, metabolic conditions, and disordered eating',
      'Collaborate on complex mind–body plans',
    ],
    collaboration:
      'Work with endocrinologists, psychiatrists, and therapists — contributing the nutritional layer of a unified treatment plan.',
    offer: [
      'Integrated mind–body clinical pathway',
      'Collaboration with psychiatry, endocrinology, and therapy',
      'Shared clinical record with full medical context',
      'Clinical supervision and peer review',
      'Flexible work across outpatient and remote settings',
      'Contribution to a new model of integrated care',
    ],
    cta: { label: 'Join our network', href: null /* set below from slug */ },
  },
  {
    slug: 'neuro-rheumatologist',
    group: 'Care-Givers',
    Icon: Microscope,
    title: 'Neuro Rheumatologist',
    tagline: 'Specialised care at the intersection of autoimmunity, neurology, and mental health.',
    intro:
      "A specialist role within AUMHUM's mind–body care programme — working at the intersection of neuroimmunology, rheumatology, and psychiatric presentations where inflammation, autoimmunity, and cognition overlap.",
    role: [
      'Evaluate neuroimmune and rheumatological contributors to mental health',
      'Partner with neurology, endocrinology, and psychiatry on complex cases',
      'Review cases involving fatigue, pain, and cognitive symptoms',
      'Contribute to multi-disciplinary case reviews',
    ],
    collaboration:
      'You work at the boundary of specialties — supported by a shared clinical record and integrated care team.',
    offer: [
      'Multi-disciplinary clinical collaboration',
      'Structured mind–body clinical pathway',
      'Shared clinical record across specialists',
      'Measurement-based care tooling',
      'Clinical supervision and peer review',
      'Opportunity to contribute to integrated care research',
    ],
    cta: { label: 'Join our network', href: null /* set below from slug */ },
  },
  {
    slug: 'therapist',
    group: 'Care-Givers',
    Icon: Users,
    title: 'Therapist',
    tagline: 'Deliver therapy within a coordinated care team.',
    intro:
      'Therapists at AUMHUM deliver structured therapy within a clinical care team — with supervision, a shared clinical record, and access to specialist collaboration when cases require it.',
    role: [
      'Deliver structured therapy matched to clinical presentation',
      'Participate in supervision and clinical development',
      'Collaborate with psychiatrists and medical specialists',
      'Support measurement-based tracking of outcomes',
    ],
    collaboration:
      'Work with clinical psychologists, psychiatrists, and the wider care team — inside a shared clinical record and continuity-first pathway.',
    offer: [
      'Structured therapy protocols',
      'Clinical supervision and continuing development',
      'Psychiatric and medical collaboration',
      'Measurement-based outcome tracking',
      'Work across outpatient, rehabilitation, and remote settings',
      'Career progression inside a clinical system',
    ],
    cta: { label: 'Join our network', href: null /* set below from slug */ },
  },
]

// Point every role's "Apply" CTA at the new clean URL (/join/:slug). The
// RolePage in AumhumSite reads this to render the primary button, and the
// JoinPage is what the user lands on from that click.
for (const role of DOCTORS) {
  role.cta.href = `/join/${role.slug}`
}
for (const role of CAREGIVERS) {
  role.cta.href = `/join/${role.slug}`
}

export const DOCTOR_NAV = [
  { to: '/doctors/psychiatrist', label: 'Psychiatrist', sub: 'Diagnosis, medication, and psychiatric oversight' },
  { to: '/doctors/pediatrician', label: 'Pediatrician', sub: 'Childhood developmental and behavioural care' },
  { to: '/doctors/endocrinologist', label: 'Endocrinologist', sub: 'Hormonal and metabolic drivers of mental health' },
  { to: '/doctors/neurologist', label: 'Neurologist', sub: 'Cognitive, sleep, and neurological overlap' },
  { to: '/doctors/rheumatologist', label: 'Rheumatologist', sub: 'Autoimmune drivers of mood and cognition' },
]

export const CAREGIVER_NAV = [
  { to: '/care-givers/clinical-psychologist', label: 'Clinical Psychologist', sub: 'Structured therapy and clinical assessment' },
  { to: '/care-givers/counsellor', label: 'Counsellor', sub: 'Counselling support inside a clinical system' },
  { to: '/care-givers/nutritionist', label: 'Nutritionist', sub: 'Nutrition in mental and endocrine health' },
  { to: '/care-givers/neuro-rheumatologist', label: 'Neuro Rheumatologist', sub: 'Neuroimmune, rheumatological, and mental health overlap' },
  { to: '/care-givers/therapist', label: 'Therapist', sub: 'Therapy within a coordinated care team' },
]
